import { alpha, Box, FormControl, IconButton, MenuItem, Select, styled, tooltipClasses, Tooltip, TooltipProps, Typography, useMediaQuery, Button } from "@mui/material";
import { isOrtexApp } from "@/lib/utils";
import { useEffect, useRef, useState, type JSX } from "react";
import { useAtom, useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useDebounce, useUpdateEffect } from "react-use";
import dayjs from "dayjs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import SaveIcon from "@mui/icons-material/Save";
import { authenticate, saveSettings } from "@/controllers/auth";
import { getPositionList, getStockShortsChanges } from "@/controllers/stock-shorts";
import { backgroundColorState, ortexColorState, textColorState, userState } from "@/lib/store";
import { Stock } from "@/models/stock";
import Module from "@/components/module/Module";
import StockLiveShortInterestData from "../stock-live-short-interest-data/StockLiveShortInterestData";
import ShortInterestPositionsChart from "../short-interest-positions-chart/ShortInterestPositionsChart";
import EsmaBoxes from "./EsmaBoxes";
import { DataGrid } from "../data-grid/DataGrid";
import StockShortsCards from "../stock-shorts-cards/StockShortsCards";
import SnackBar from "../snack-bar/SnackBar";
import Tabs, { TabItem } from "../tabs/Tabs";
import useStockShortsCards from "./hooks/useStockShortsCards";
import styles from "./StockShortsDailyInterest.module.scss";
import dynamic from "next/dynamic";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";

const StockChart = dynamic(() => import("@/components/ortex-charts/StockChart"), { ssr: false });

type Props = {
    stock: Stock;
};

const tabs: TabItem[] = [
    { title: "Current Flagged Short Interest Positions", value: "Current Flagged Short Interest Positions" },
    { title: "Official Short Interest Position Changes", value: "Official Short Interest Position Changes" },
];

interface CustomTooltipProps extends TooltipProps {
    backgroundColor: string;
    textColor: string;
    ortexColor: string;
}

const CustomTooltip = styled(({ className, ...props }: CustomTooltipProps) => <Tooltip placement="left-start" arrow open {...props} classes={{ popper: className }} />)(({ theme, backgroundColor, textColor, ortexColor }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor,
        color: textColor,
        maxWidth: 220,
        border: "1px dashed",
        borderColor: alpha(textColor, 0.1),
        padding: "12px",
        "& a": {
            color: ortexColor,
            textDecoration: "underline",
            fontWeight: 600,
        },
    },
    [`& .${tooltipClasses.arrow}`]: {
        color: backgroundColor,
        "&::before": {
            border: "1px dashed",
            borderColor: alpha(textColor, 0.1),
            backgroundColor: backgroundColor,
        },
    },
    zIndex: 5,
}));

const useHoverState = (initialState = false, delay = 500) => {
    const [isHovering, setIsHovering] = useState(initialState);
    const [debouncedHover, setDebouncedHover] = useState(initialState);

    useDebounce(
        () => {
            setDebouncedHover(isHovering);
        },
        delay,
        [isHovering]
    );

    return {
        isHovering,
        debouncedHover,
        setIsHovering,
    };
};

const StockShortsDailyInterest = ({ stock }: Props) => {
    const isMobile = useMediaQuery("(max-width:1280px)");
    const isDesktop = useMediaQuery("(min-width:1400px)");
    const isBigScreen = useMediaQuery("(min-width:3840px)");
    const isLandscape = useMediaQuery("(orientation: landscape)");

    const textColor = useAtomValue(textColorState);
    const [user, setUser] = useAtom(userState);
    const ortexColor = useAtomValue(ortexColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const router = useRouter();
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();

    // Populated by TradingViewShortsChart so the panel-header share icon can
    // trigger the same share-card flow as the chart's own Share button.
    const shareHandlerRef = useRef<(() => void) | null>(null);

    const [isExpanded, setIsExpanded] = useState(false);
    const [regshoFlag, setRegshoFlag] = useState<false | JSX.Element>(false);
    const [divHeight, setDivHeight] = useState(null);
    const [activeTab, setActiveTab] = useState<string>();
    const [lastUpdateDay, setLastUpdateDay] = useState(null);
    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const divRef = useRef(null);

    const [showInitialTooltip, setShowInitialTooltip] = useState(false);
    const { isHovering, debouncedHover, setIsHovering } = useHoverState(false);

    const tickerName = `${stock.exchangesymbol}:${stock.ticker}`;

    const { data, keys, isMounted, loading, obfuscated, timeRanges, activeTimeRange, shortInterest, setActiveTimeRange } = useStockShortsCards(stock);

    useEffect(() => {
        if (!activeTab) {
            if (user.settings.module_settings["s/[exchange]/[ticker]/short-interest"]?.activeTab) {
                setActiveTab(user.settings.module_settings["s/[exchange]/[ticker]/short-interest"]?.activeTab);
            } else {
                setActiveTab(tabs[0].value);
            }
        }
    }, [activeTab]);

    useEffect(() => {
        if (divRef.current) setDivHeight(divRef.current.offsetHeight);
    });

    useUpdateEffect(() => {
        const date = dayjs.utc(shortInterest["Date"] as number);

        if (!date.isValid()) setLastUpdateDay("N/A");
        else if (dayjs.utc().diff(date, "days") > 5) setLastUpdateDay(date.format("DD MMM"));
        else if ([0, 6].includes(date.utc().day())) setLastUpdateDay("Friday");
        else if (date.utc().day() === 5) setLastUpdateDay("Monday");
        else setLastUpdateDay(date.format("dddd"));
    }, [shortInterest]);

    useEffect(() => {
        const onRouteChangeStart = () => {
            setLastUpdateDay("Loading...");
        };
        router.events.on("routeChangeStart", onRouteChangeStart);

        const tooltipTimeout = setTimeout(() => {
            setShowInitialTooltip(true);
        }, 2000);

        return () => {
            router.events.off("routeChangeStart", onRouteChangeStart);
            clearTimeout(tooltipTimeout);
        };
    }, []);

    const handleChange = (event: React.MouseEvent<HTMLButtonElement>, newAlignment: "Current Flagged Short Interest Positions" | "Official Short Interest Position Changes") => {
        setActiveTab(newAlignment);
    };

    const handleSaveTimeRange = () => {
        if (user.subscription_plan === "ORTEX Visitor") return setSnackBar({ open: true, text: "You need to be a registered user to save your default time range", severity: "error" });
        const moduleName = "StockShorts";
        const route = "/boxes/time-range";
        saveSettings(moduleName, route, { value: activeTimeRange })
            .then(authenticate)
            .then(() => {
                setUser((prevUser) => ({
                    ...prevUser,
                    settings: {
                        ...prevUser.settings,
                        module_settings: {
                            ...prevUser.settings?.module_settings,
                            [moduleName + "->" + route]: {
                                ...prevUser.settings?.module_settings?.[moduleName + "->" + route],
                                value: activeTimeRange,
                            },
                        },
                    },
                }));
            })
            .then(() => {
                setSnackBar({ open: true, text: `This is now your default time range and will be applied next time you go to this page`, severity: "success" });
            });
    };

    const renderModule = (type: "ESMA" | "FINRA" | null) => {
        switch (type) {
            case "FINRA":
                return (
                    <Module title={`Official Short Interest Positions in ${tickerName}`} help={{ moduleId: "official_short_interest_positions", modulePage: "V2_stock_short_interest" }} noPadding={{ source: "body", type: "mobile" }}>
                        <ShortInterestPositionsChart stock={stock} isExpanded={isExpanded} />
                    </Module>
                );
            case "ESMA":
                return (
                    <Module title={`Official Flagged Short Interest for ${tickerName}`}>
                        <Box className={styles.container}>
                            <Box
                                sx={{
                                    pb: 1,
                                    maxHeight: "120px",
                                    "@media (max-width: 1279px)": {
                                        pb: 5,
                                    },
                                    "@media (max-width: 416px)": {
                                        pb: 10,
                                    },
                                }}
                            >
                                <EsmaBoxes stock={stock} />
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", mt: 1 }}>
                                <Tabs tabs={tabs} activeTab={activeTab} module={"short-interest"} route={"s/[exchange]/[ticker]/short-interest"} setState={setActiveTab} handleChange={handleChange} alignment="center" setDefault />
                            </Box>
                            <Box sx={{ height: "100%" }}>
                                {activeTab === tabs[0].title ? (
                                    <DataGrid fetcher={getPositionList} key="changes" regionId={stock.tradingitem_id} tableId="shorts-daily-position-list" />
                                ) : (
                                    <DataGrid fetcher={getStockShortsChanges} key="list" regionId={stock.tradingitem_id} tableId="shorts-daily-changes" />
                                )}
                            </Box>
                        </Box>
                    </Module>
                );
            default:
                return (
                    <Module noHeader>
                        <p className={styles["no-data"]}>There is no regulatory reported data for this stock</p>
                    </Module>
                );
        }
    };

    useEffect(() => {
        isDesktop && setIsExpanded(true);
    }, [isDesktop]);

    const getHeightLiveDataModule = () => {
        if (isBigScreen) return 9;
        return 15;
    };

    return (
        <div className={styles.StockShortsDailyInterest}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, p: 1 }} className={styles["header"]}>
                <Typography variant="subtitle2" component="p" className={styles["last-updated"]}>
                    Updated:
                    <span> {lastUpdateDay}</span> - Data as per previous close
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} className={styles["time-range"]}>
                    {Object.keys(timeRanges)?.length ? (
                        <>
                            <Typography variant="subtitle2" component="span" sx={{ color: textColor }}>
                                Time Range:{" "}
                            </Typography>
                            <FormControl>
                                <Select
                                    variant="standard"
                                    sx={{
                                        typography: "subtitle2",
                                        padding: 0,
                                        width: "fit-content",
                                        backgroundColor: "transparent",
                                        ".MuiSvgIcon-root ": {
                                            fill: textColor,
                                        },
                                    }}
                                    inputProps={{ "aria-label": "Time range" }}
                                    value={activeTimeRange}
                                    onChange={(e) => setActiveTimeRange(e.target.value)}
                                    disabled={obfuscated}
                                >
                                    {obfuscated ? (
                                        <MenuItem key={"3 months"} value={"3m"}>
                                            3 months
                                        </MenuItem>
                                    ) : (
                                        Object.entries(timeRanges)?.map((option) => (
                                            <MenuItem key={option[1]} value={option[0]}>
                                                {option[1]}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                            <Tooltip arrow describeChild title={"Save time range as default"}>
                                <span>
                                    <Button aria-label="Save time range as default" sx={{ width: "fit-content", minWidth: "25px", paddingX: 0 }} onClick={handleSaveTimeRange} disabled={obfuscated || user.subscription_plan === "ORTEX Visitor"}>
                                        <SaveIcon fontSize="small" />
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    ) : null}
                </Box>
            </Box>
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />

            <div className={styles.cards}>
                <StockShortsCards data={data} keys={keys} isMounted={isMounted} loading={loading} obfuscated={obfuscated} timeRanges={timeRanges} activeTimeRange={activeTimeRange} shortInterest={shortInterest} />
            </div>
            <div className={styles.bottom}>
                <style jsx>
                    {`
                        .${styles["live-data"]} {
                            height: ${isMobile || isExpanded ? "auto" : `${getHeightLiveDataModule()}%`} !important;
                            min-height: 80px;
                        }
                        .${styles["regulatory-data"]} {
                            height: ${isMobile ? (stock.shorts_type === "FINRA" ? "400px" : "auto") : `calc(100% - ${divHeight > 80 ? divHeight : 80}px)`} !important;
                            ${isMobile ? "min-height: 300px;" : ""}
                        }
                        .${styles["chart"]} {
                            ${isMobile && isLandscape ? "height: clamp(400px, 75vh, 640px); overflow: hidden;" : ""}
                        }
                    `}
                </style>

                <div className={styles.chart}>
                    {isMobile && !isLandscape ? (
                        <Module title={`Short Interest Charts for ${tickerName}`}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "200px",
                                    textAlign: "center",
                                    p: 2,
                                }}
                            >
                                <Typography variant="body1" sx={{ color: textColor }}>
                                    This chart is only available in landscape mode. Please rotate your device.
                                </Typography>
                            </Box>
                        </Module>
                    ) : (
                        <Module
                            title={`Short Interest Charts for ${tickerName}`}
                            help={{ moduleId: "short_interest_chart", modulePage: "V2_stock_short_interest" }}
                            noPadding={{ source: "body", type: "all" }}
                            settingsComponent={
                                ["ORTEX Free", "ORTEX Basic", "ORTEX Visitor"].includes(user?.subscription_plan) && (
                                    <IconButton aria-label="Some data is restricted" className={styles.button} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
                                        <CustomTooltip
                                            title={
                                                <Box component="span" sx={{ fontSize: "12px" }}>
                                                    Some data is restricted.{" "}
                                                    <Button
                                                        variant="text"
                                                        size="small"
                                                        onClick={() => {
                                                            trackUpgradeClick(UPGRADE_LOCATIONS.STOCK_SHORTS_DAILY_INTEREST, { plan: user?.subscription_plan });
                                                            return user.subscription_plan === "ORTEX Visitor" ? router.push("/login") : handleAppSubscriberUpgrade();
                                                        }}
                                                        sx={{ textTransform: "capitalize", p: 0, verticalAlign: "baseline" }}
                                                        component="span"
                                                    >
                                                        Upgrade now
                                                    </Button>{" "}
                                                    to unlock the latest and full historical data.
                                                    {showInitialTooltip && (
                                                        <IconButton
                                                            aria-label="Close"
                                                            size="small"
                                                            onClick={() => {
                                                                setShowInitialTooltip(false);
                                                                setIsHovering(false);
                                                            }}
                                                            sx={{
                                                                position: "absolute",
                                                                top: 0,
                                                                right: 0,
                                                                padding: 0.5,
                                                                color: "inherit",
                                                            }}
                                                        >
                                                            <CloseIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            }
                                            backgroundColor={backgroundColor}
                                            textColor={textColor}
                                            ortexColor={ortexColor}
                                            // Signed-out web visitors get the LockedValuePanel instead of the
                                            // auto-opened tooltip; hovering the icon still shows it.
                                            open={(showInitialTooltip && !(user?.subscription_plan === "ORTEX Visitor" && !isOrtexApp())) || debouncedHover}
                                        >
                                            <WarningAmberIcon style={{ color: textColor }} />
                                        </CustomTooltip>
                                    </IconButton>
                                )
                            }
                            share={{ onClick: () => shareHandlerRef.current?.(), tooltip: "Share this chart" }}
                            feedback
                        >
                            <StockChart key={stock.tradingitem_id} stock={stock} mode="shorts" chartId="short-interest" shareHandlerRef={shareHandlerRef} />
                        </Module>
                    )}
                </div>

                <div className={styles.right}>
                    <div className={styles["live-data"]} ref={divRef}>
                        <Module
                            title={`Live Short Interest Data for ${tickerName}`}
                            help={{ moduleId: "live_data", modulePage: "V2_stock_short_interest" }}
                            settingsComponent={
                                !isMobile && (
                                    <IconButton aria-label={isExpanded ? "Collapse" : "Expand"} aria-expanded={isExpanded} className={styles["expand-btn"]} onClick={() => setIsExpanded(!isExpanded)} sx={{ color: textColor, fill: textColor }}>
                                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDownIcon />}
                                    </IconButton>
                                )
                            }
                            subHeader={<>{regshoFlag}</>}
                        >
                            <StockLiveShortInterestData stock={stock} setModuleHeader={setRegshoFlag} page={"overview"} expanded={isExpanded} />
                        </Module>
                    </div>
                    <div className={styles["regulatory-data"]}>{renderModule(stock.shorts_type)}</div>
                </div>
            </div>
        </div>
    );
};

export default StockShortsDailyInterest;
